import {
  Box,
  BoxProps,
  CircularProgress,
  Grid,
  Link,
  styled,
  Typography,
} from '@mui/material';
import { FC, useEffect, useState } from 'react';

import { observer } from 'mobx-react-lite';
import { useMessagesApi, useWelcomeApi } from '../../ApiClient';
import config from '../../config';
import useMedia from '../../hooks/useMedia';
import { useStore } from '../../store/context';
import { getLogoForClient, Rebrandings } from '../../utils';

const configuration = config[process.env.REACT_APP_CLIENT || 'wbg'];
const rebrandingKey =
  process.env.REACT_APP_CLIENT && process.env.REACT_APP_CLIENT in Rebrandings
    ? (process.env.REACT_APP_CLIENT as keyof typeof Rebrandings)
    : 'wbg';
const logo = getLogoForClient()[rebrandingKey];

const RootContainer = styled(Box)<BoxProps>(({ theme }) => ({
  backgroundColor: theme.palette.secondary.light,
}));

const Header = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-start',
  minHeight: '90px',
});

const ContentContainer = styled(Box)({
  position: 'relative',
  backgroundColor: 'white',
  display: 'flex',
  flexDirection: 'column',
  justifyItems: 'center',
  alignItems: 'center',
});

const WelcomeText = styled(Box)<BoxProps>(({ theme }) => ({
  transform: 'translate(-20px)',
  position: 'absolute',
  backgroundColor: theme.palette.secondary.main,
  left: 0,
  top: 30,
  width: '250px',
  padding: '5px 10px',
  borderRadius: '2px',
  color: 'white',
}));

interface Props {
  children: React.ReactNode;
}

const BackgroundContainer: FC<Props> = observer(({ children }) => {
  const isMobile = useMedia('md');

  const { welcomeStore, messageStore } = useStore();
  const welcomeApi = useWelcomeApi();
  const messagesApi = useMessagesApi();

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    async function fetchMessages() {
      if (welcomeStore.currentEstate) {
        try {
          const { data } = await messagesApi.getAllCustomerMessages(
            welcomeStore.currentEstate.customerId,
          );
          messageStore.setMessages(data);
        } catch (error) {
          console.log(error);
        }
      }
    }

    async function fetchWelcomeData() {
      try {
        const { data } = await welcomeApi.getWelcomeData(
          welcomeStore.currentEstateId,
        );
        welcomeStore.setWelcomeData(data);
        fetchMessages();
        setLoading(false);
      } catch (error) {
        // TODO: add error handling
        console.log(error);
      }
    }

    if (!welcomeStore.getWelcomeData()) fetchWelcomeData();
  }, [welcomeApi, messagesApi]);

  return (
    <RootContainer
      sx={{
        paddingLeft: isMobile ? 0 : '2%',
        paddingRight: isMobile ? 0 : '2%',
      }}
    >
      <Header>
        <Box
          marginLeft={isMobile ? 1 : 0}
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Link href={process.env.REACT_APP_HOMEPAGE_URL}>
            <Box
              component="img"
              height="auto"
              style={{
                height: process.env?.REACT_APP_CLIENT?.includes('wbg')
                  ? 40
                  : 80,
              }}
              maxWidth={isMobile ? 250 : 300}
              maxHeight={
                process.env?.REACT_APP_CLIENT?.includes('wbg') ? 40 : 80
              }
              src={logo}
            />
          </Link>
        </Box>
      </Header>
      <ContentContainer>
        <WelcomeText
          style={{
            display: isMobile ? 'none' : 'block',
          }}
          id="textcontainerwelcome"
        >
          <Typography
            variant="body2"
            sx={{
              textTransform: 'uppercase',
              lineHeight: 2,
              fontSize: 12,
              letterSpacing: 1,
              textAlign: 'left',
            }}
          >
            {`${configuration.title}`}
          </Typography>
        </WelcomeText>

        <Grid container>
          <Grid item xs={12}>
            {loading ? (
              <Box
                width="100%"
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="90vh"
              >
                <CircularProgress color="secondary" />
              </Box>
            ) : (
              children
            )}
          </Grid>
        </Grid>
      </ContentContainer>{' '}
    </RootContainer>
  );
});

export default BackgroundContainer;
